import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  CardActionArea,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  Checkbox,
  FormControlLabel,
  IconButton,
  Fade,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon, Star as StarIcon, Flag as FlagIcon, Add as AddIcon, CalendarToday as CalendarIcon, ContentCopy as ContentCopyIcon, History as HistoryIcon, Delete as DeleteIcon, Sync as SyncIcon, Edit as EditIcon, Check as CheckIcon, MoreVert as MoreVertIcon } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { Target } from '../types/models';
import { targetApi } from '../api/apiService';
import { useUser } from '../context/UserContext';
import { categories as badgeCategories } from '../data/energyCategories';

type TargetType = 'small' | 'medium' | 'large';

const TARGET_CONFIG: Record<TargetType, { points: number; label: string; description: string; color: string; bgColor: string }> = {
  small: { points: 5, label: 'Small', description: 'Quick wins', color: '#059669', bgColor: '#ecfdf5' },
  medium: { points: 10, label: 'Medium', description: 'Moderate effort', color: '#d97706', bgColor: '#fffbeb' },
  large: { points: 20, label: 'Large', description: 'Big achievement', color: '#dc2626', bgColor: '#fef2f2' }
};

const getTargetTypeByPoints = (points: number): TargetType => {
  if (points <= 5) return 'small';
  if (points <= 10) return 'medium';
  return 'large';
};

const COMPLETED_PAGE_SIZE = 20;

const TargetLogPage = () => {
  const { userId } = useParams<{ userId: string }>();
  const { refreshUser } = useUser();
  const [targets, setTargets] = useState<Target[]>([]);
  const [templates, setTemplates] = useState<Target[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [displayedCount, setDisplayedCount] = useState(COMPLETED_PAGE_SIZE);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<TargetType | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [duplicating, setDuplicating] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [isQuickAdd, setIsQuickAdd] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [targetToDelete, setTargetToDelete] = useState<Target | null>(null);
  const [completeConfirmOpen, setCompleteConfirmOpen] = useState(false);
  const [targetToComplete, setTargetToComplete] = useState<Target | null>(null);
  const [completing, setCompleting] = useState(false);
  const [recurring, setRecurring] = useState(false);
  const [showDescription, setShowDescription] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuTarget, setMenuTarget] = useState<Target | null>(null);
  const [completedMenuAnchor, setCompletedMenuAnchor] = useState<null | HTMLElement>(null);
  const [completedMenuTarget, setCompletedMenuTarget] = useState<Target | null>(null);

  useEffect(() => {
    if (userId) {
      fetchTargets();
      fetchTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchTargets = async () => {
    try {
      setLoading(true);
      const response = await targetApi.getAll(userId!);
      // Sort by createdAt so recurring targets stay in position after refresh
      const sorted = ((response.data || []) as Target[]).slice().sort((a, b) => {
        // Recurring targets first
        if (a.recurring && !b.recurring) return -1;
        if (!a.recurring && b.recurring) return 1;
        // Within each group, newest first
        return new Date(b.createdAt!).getTime() - new Date(a.createdAt!).getTime();
      });
      setTargets(sorted);
      setDisplayedCount(COMPLETED_PAGE_SIZE);
    } catch (err) {
      setError('Failed to load targets');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await targetApi.getTemplates();
      setTemplates(response.data || []);
    } catch (err) {
      console.error('Failed to load templates:', err);
    }
  };

  const handleOpenDialog = (type: TargetType) => {
    setSelectedType(type);
    setName('');
    setDescription('');
    setIsQuickAdd(false);
    setDialogOpen(true);
  };

  const handleQuickAdd = (target: Target) => {
    const type = getTargetTypeByPoints(target.points);
    setSelectedType(type);
    setName(target.name);
    setDescription(target.description || '');
    setIsQuickAdd(true);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedType(null);
    setName('');
    setDescription('');
    setRecurring(false);
    setShowDescription(false);
    setSelectedCategories([]);
  };

  const handleSubmit = async () => {
    if (!selectedType || !name.trim()) return;

    try {
      setSubmitting(true);
      const newTarget = {
        name: name.trim(),
        description: description.trim(),
        points: TARGET_CONFIG[selectedType].points,
        categories: selectedCategories,
        userId: userId,
        completed: false,
        recurring: recurring,
      };

      await targetApi.create(newTarget);
      handleCloseDialog();
      fetchTargets();
    } catch (err) {
      setError('Failed to create target');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDuplicateTarget = async (target: Target) => {
    try {
      setDuplicating(target.id!);
      const newTarget = {
        name: target.name,
        description: target.description,
        points: target.points,
        categories: target.categories,
        userId: userId,
        completed: false,
        recurring: target.recurring || false,
      };

      const response = await targetApi.create(newTarget);
      setTargets(prev => response.data ? [...prev, response.data] : prev);
    } catch (err) {
      setError('Failed to duplicate target');
      console.error(err);
    } finally {
      setDuplicating(null);
    }
  };

  const handleMarkComplete = async (id: string) => {
    try {
      setCompleting(true);
      const response = await targetApi.markCompleted(id);
      refreshUser();

      setTargets(prev => {
        const idx = prev.findIndex(t => t.id === id);
        if (idx === -1 || !response.data) return prev;

        const updated = [...prev];
        if (response.data.recurring) {
          // Backend returns a fresh copy (completed: false) that stays in pending list
          updated[idx] = response.data;
        } else {
          // Move completed target to end of list (it will be sorted to top of completed section)
          updated.splice(idx, 1);
          updated.push({ ...response.data, completed: true, completedAt: new Date() });
        }
        return updated;
      });
    } catch (err: any) {
      setError('Failed to mark target as complete');
    } finally {
      setCompleting(false);
    }
  };

  const handleOpenCompleteConfirm = (target: Target) => {
    setTargetToComplete(target);
    setCompleteConfirmOpen(true);
  };

  const handleCloseCompleteConfirm = () => {
    setCompleteConfirmOpen(false);
    setTargetToComplete(null);
  };

  const handleConfirmComplete = async () => {
    if (!targetToComplete?.id) return;
    await handleMarkComplete(targetToComplete.id);
    setTargetToComplete(null);
    setCompleteConfirmOpen(false);
  };

  const handleDeleteTarget = async (id: string) => {
    try {
      setDeleting(id);
      await targetApi.delete(id);
      setTargets(prev => prev.filter(t => t.id !== id));
    } catch (err) {
      setError('Failed to delete target');
      console.error(err);
    } finally {
      setDeleting(null);
    }
  };

  const handleOpenDeleteConfirm = (target: Target) => {
    setTargetToDelete(target);
    setDeleteConfirmOpen(true);
  };

  const handleCloseDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setTargetToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!targetToDelete?.id) return;
    await handleDeleteTarget(targetToDelete.id);
    handleCloseDeleteConfirm();
  };

  const pendingTargets = targets.filter(t => !t.completed);
  const completedTargets = targets
    .filter(t => t.completed)
    .sort((a, b) => {
      const dateA = a.completedAt ? new Date(a.completedAt).getTime() : new Date(a.createdAt!).getTime();
      const dateB = b.completedAt ? new Date(b.completedAt).getTime() : new Date(b.createdAt!).getTime();
      return dateB - dateA;
    });

  const visibleCompleted = completedTargets.slice(0, displayedCount);
  const hasMore = completedTargets.length > displayedCount;

  // Get unique target templates from all completed targets in the family
  const recentTemplates = templates
    .slice()
    .reverse()
    .filter((target, index, self) =>
      self.findIndex(t => t.name === target.name) === index
    )
    .slice(0, 6);

  if (!userId) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Typography color="text.secondary">No user selected</Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>{error}</Alert>}

      {/* Quick Add from History */}
      {recentTemplates.length > 0 && (
        <Card sx={{ mb: 3, borderRadius: 3, overflow: 'hidden' }}>
          <Box sx={{
            background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)',
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}>
            <Box sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              borderRadius: 2,
              p: 1,
              display: 'flex'
            }}>
              <HistoryIcon sx={{ color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 700 }}>
                Quick Add
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                Reuse your previous targets
              </Typography>
            </Box>
          </Box>
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {recentTemplates.map((target) => {
                const type = getTargetTypeByPoints(target.points);
                const config = TARGET_CONFIG[type];
                return (
                  <Chip
                    key={target.id}
                    label={target.name}
                    onClick={() => handleQuickAdd(target)}
                    sx={{
                      bgcolor: config.bgColor,
                      color: config.color,
                      border: `1px solid ${config.color}30`,
                      fontWeight: 500,
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: config.color,
                        color: 'white',
                        borderColor: config.color
                      }
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        </Card>
      )}

      {/* Create Target Section */}
      <Card sx={{ mb: 4, borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5
        }}>
          <Box sx={{
            bgcolor: 'rgba(255,255,255,0.2)',
            borderRadius: 2,
            p: 1,
            display: 'flex'
          }}>
            <AddIcon sx={{ color: 'white' }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 700 }}>
              Create New Target
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
              Choose a size and set your goal
            </Typography>
          </Box>
        </Box>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={2}>
            {(Object.keys(TARGET_CONFIG) as TargetType[]).map((type) => {
              const config = TARGET_CONFIG[type];
              return (
                <Grid item xs={12} sm={4} key={type}>
                  <Card
                    sx={{
                      borderRadius: 2,
                      border: '2px solid',
                      borderColor: 'transparent',
                      transition: 'all 0.2s ease',
                      cursor: 'pointer',
                      bgcolor: config.bgColor,
                      '&:hover': {
                        borderColor: config.color,
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 24px -8px ${config.color}40`
                      }
                    }}
                  >
                    <CardActionArea onClick={() => handleOpenDialog(type)} sx={{ p: 2 }}>
                      <Box sx={{ textAlign: 'center', py: 1 }}>
                        <Typography variant="overline" sx={{ color: config.color, fontWeight: 700, letterSpacing: 1 }}>
                          {config.label}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5, my: 1 }}>
                          {Array.from({ length: config.points / 5 }).map((_, i) => (
                            <StarIcon key={i} sx={{ fontSize: 24, color: config.color }} />
                          ))}
                        </Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: config.color }}>
                          {config.points}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          points
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          {config.description}
                        </Typography>
                      </Box>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>

      {/* Pending Targets */}
      {pendingTargets.length > 0 && (
        <Card sx={{ mb: 4, borderRadius: 3, overflow: 'hidden' }}>
          <Box sx={{
            background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                borderRadius: 2,
                p: 1,
                display: 'flex'
              }}>
                <FlagIcon sx={{ color: 'white' }} />
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 700 }}>
                  Active Targets
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                  {pendingTargets.length} goal{pendingTargets.length !== 1 ? 's' : ''} in progress
                </Typography>
              </Box>
            </Box>
            <Chip
              label={pendingTargets.reduce((sum, t) => sum + t.points, 0) + ' pts'}
              sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'white', fontWeight: 600 }}
            />
          </Box>
          <Box sx={{ p: 2 }}>
            {pendingTargets.map((target, index) => {
              const type = getTargetTypeByPoints(target.points);
              const config = TARGET_CONFIG[type];
              return (
              <Fade in key={target.id}>
                <Card
                  sx={{
                    mb: index < pendingTargets.length - 1 ? 2 : 0,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                    }
                  }}
                >
                  <Box sx={{ p: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Box sx={{ position: 'relative', flexShrink: 0 }}>
                      <Box sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: config.bgColor
                      }}>
                        <Typography variant="body2" fontWeight={700} sx={{ color: config.color }}>
                          {target.points}
                        </Typography>
                      </Box>
                      {target.recurring && (
                        <Box sx={{
                          position: 'absolute',
                          top: -4,
                          right: -4,
                          bgcolor: 'white',
                          borderRadius: '50%',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.15)'
                        }}>
                          <SyncIcon sx={{ fontSize: 12, color: 'primary.main' }} />
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                        {target.name}
                      </Typography>
                      {target.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, wordBreak: 'break-word' }}>
                          {target.description}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <CalendarIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                        <Typography variant="caption" color="text.disabled">
                          {new Date(target.createdAt).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0, flexShrink: 0 }}>
                      <Tooltip title="Mark complete">
                        <IconButton
                          onClick={() => handleOpenCompleteConfirm(target)}
                          disabled={completing}
                          size="small"
                          sx={{ color: 'success.main' }}
                        >
                          <CheckCircleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="More actions">
                        <IconButton
                          onClick={(e) => {
                            setMenuAnchor(e.currentTarget);
                            setMenuTarget(target);
                          }}
                          size="small"
                          sx={{ color: 'text.secondary' }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Card>
              </Fade>
            );
            })}
          </Box>
        </Card>
      )}

      {/* Completed Targets */}
      {completedTargets.length > 0 && (
        <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Box sx={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                borderRadius: 2,
                p: 1,
                display: 'flex'
              }}>
                <CheckCircleIcon sx={{ color: 'white' }} />
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 700 }}>
                  Completed
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                  {completedTargets.length} goal{completedTargets.length !== 1 ? 's' : ''} achieved
                </Typography>
              </Box>
            </Box>
            <Chip
              label={completedTargets.reduce((sum, t) => sum + t.points, 0) + ' pts earned'}
              sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'white', fontWeight: 600 }}
            />
          </Box>
          <Box sx={{ p: 2 }}>
            {visibleCompleted.map((target, index) => {
              const type = getTargetTypeByPoints(target.points);
              const config = TARGET_CONFIG[type];
              return (
              <Card
                key={target.id}
                sx={{
                  mb: index < visibleCompleted.length - 1 ? 2 : 0,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                  }
                }}
              >
                <Box sx={{ p: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Box sx={{ position: 'relative', flexShrink: 0 }}>
                    <Box sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: config.bgColor
                    }}>
                      <Typography variant="body2" fontWeight={700} sx={{ color: config.color }}>
                        {target.points}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight={600} color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                      {target.name}
                    </Typography>
                    {target.description && (
                      <Typography variant="body2" color="text.disabled" sx={{ wordBreak: 'break-word' }}>
                        {target.description}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                      <CalendarIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                      <Typography variant="caption" color="text.disabled">
                        Completed {target.completedAt ? new Date(target.completedAt).toLocaleDateString() : ''}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0, flexShrink: 0 }}>
                    <Tooltip title="Create again">
                      <IconButton
                        onClick={() => handleQuickAdd(target)}
                        size="small"
                        sx={{ color: 'success.main' }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="More actions">
                      <IconButton
                        onClick={(e) => {
                          setCompletedMenuAnchor(e.currentTarget);
                          setCompletedMenuTarget(target);
                        }}
                        size="small"
                        sx={{ color: 'text.secondary' }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Card>
            );
            })}
            {hasMore && (
              <Button
                fullWidth
                onClick={() => setDisplayedCount(prev => prev + COMPLETED_PAGE_SIZE)}
                sx={{ mt: 1 }}
              >
                Load More ({completedTargets.length - displayedCount} more)
              </Button>
            )}
          </Box>
        </Card>
      )}

      {/* Empty State */}
      {targets.length === 0 && (
        <Card sx={{ borderRadius: 3, p: 6, textAlign: 'center' }}>
          <FlagIcon sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No targets yet
          </Typography>
          <Typography variant="body2" color="text.disabled">
            Create your first target above to get started!
          </Typography>
        </Card>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          {selectedType && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {Array.from({ length: TARGET_CONFIG[selectedType].points / 5 }).map((_, i) => (
                  <StarIcon key={i} sx={{ fontSize: 20, color: TARGET_CONFIG[selectedType].color }} />
                ))}
              </Box>
              <Typography variant="h6" fontWeight={700}>
                {isQuickAdd ? 'Quick Add Target' : `New ${TARGET_CONFIG[selectedType].label} Target`}
              </Typography>
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          {isQuickAdd ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Target Name
              </Typography>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                {name}
              </Typography>
            </Box>
          ) : (
            <TextField
              fullWidth
              label="Target Name"
              placeholder="What do you want to achieve?"
              value={name}
              onChange={(e) => setName(e.target.value)}
              margin="normal"
              required
              autoFocus
            />
          )}
          {!isQuickAdd && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={recurring}
                  onChange={(e) => setRecurring(e.target.checked)}
                  size="small"
                />
              }
              label="Recurring target"
              sx={{ mt: 1 }}
            />
          )}
          {!showDescription ? (
            <Button
              size="small"
              startIcon={<EditIcon sx={{ fontSize: 16 }} />}
              onClick={() => setShowDescription(true)}
              sx={{ mt: 1, color: 'text.secondary', textTransform: 'none', minWidth: 'auto', p: '4px 8px' }}
            >
              Add description
            </Button>
          ) : (
            <Box sx={{ mt: 1 }}>
              <TextField
                fullWidth
                label="Description"
                placeholder="Add some details..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                margin="normal"
                multiline
                rows={2}
                autoFocus
                InputProps={{
                  endAdornment: (
                    <IconButton size="small" onClick={() => { setShowDescription(false); setDescription(''); }}>
                      <CheckIcon fontSize="small" />
                    </IconButton>
                  ),
                }}
              />
            </Box>
          )}
          {!isQuickAdd && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Categories (optional)
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {badgeCategories.map((cat) => {
                  const isSelected = selectedCategories.includes(cat.key);
                  return (
                    <Chip
                      key={cat.key}
                      label={`${cat.icon || ''} ${cat.label || cat.key}`}
                      onClick={() => {
                        setSelectedCategories(prev =>
                          isSelected
                            ? prev.filter(c => c !== cat.key)
                            : [...prev, cat.key]
                        );
                      }}
                      sx={{
                        bgcolor: isSelected ? (cat.color || '#6366f1') + '20' : 'grey.100',
                        color: isSelected ? (cat.color || '#6366f1') : 'text.secondary',
                        border: `1px solid ${isSelected ? (cat.color || '#6366f1') : 'transparent'}`,
                        fontWeight: isSelected ? 600 : 400,
                        '&:hover': {
                          bgcolor: isSelected ? cat.color + '30' : 'grey.200',
                        }
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          )}
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={`${TARGET_CONFIG[selectedType!]?.points || 0} points on completion`}
              size="small"
              sx={{ bgcolor: TARGET_CONFIG[selectedType!]?.bgColor, color: TARGET_CONFIG[selectedType!]?.color }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseDialog} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || !name.trim()}
            sx={{ px: 3 }}
          >
            {submitting ? <CircularProgress size={24} /> : 'Create Target'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleCloseDeleteConfirm}>
        <DialogTitle>Delete Target</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{targetToDelete?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseDeleteConfirm} disabled={deleting === targetToDelete?.id}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleting === targetToDelete?.id}
          >
            {deleting === targetToDelete?.id ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Complete Confirmation Dialog */}
      <Dialog open={completeConfirmOpen} onClose={handleCloseCompleteConfirm}>
        <DialogTitle>Complete Target</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Mark "{targetToComplete?.name}" as complete? You will earn {targetToComplete?.points} points!
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseCompleteConfirm} disabled={completing}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleConfirmComplete}
            disabled={completing}
          >
            {completing ? <CircularProgress size={24} /> : 'Complete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => { setMenuAnchor(null); setMenuTarget(null); }}
        PaperProps={{ sx: { borderRadius: 2, minWidth: 140 } }}
      >
        {menuTarget && (
          <>
            <MenuItem onClick={() => {
              handleDuplicateTarget(menuTarget);
              setMenuAnchor(null);
              setMenuTarget(null);
            }} disabled={duplicating === menuTarget.id}>
              <ListItemIcon>
                <ContentCopyIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Duplicate</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => {
              handleOpenDeleteConfirm(menuTarget);
              setMenuAnchor(null);
              setMenuTarget(null);
            }} sx={{ color: 'error.main' }}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Completed Targets Actions Menu */}
      <Menu
        anchorEl={completedMenuAnchor}
        open={Boolean(completedMenuAnchor)}
        onClose={() => { setCompletedMenuAnchor(null); setCompletedMenuTarget(null); }}
        PaperProps={{ sx: { borderRadius: 2, minWidth: 140 } }}
      >
        {completedMenuTarget && (
          <>
            <MenuItem onClick={() => {
              handleDuplicateTarget(completedMenuTarget);
              setCompletedMenuAnchor(null);
              setCompletedMenuTarget(null);
            }} disabled={duplicating === completedMenuTarget.id}>
              <ListItemIcon>
                <ContentCopyIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Duplicate</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => {
              handleOpenDeleteConfirm(completedMenuTarget);
              setCompletedMenuAnchor(null);
              setCompletedMenuTarget(null);
            }} sx={{ color: 'error.main' }}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
    </Box>
  );
};

export default TargetLogPage;